import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Bilingue } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import type { ApercusSignes, Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

/**
 * Artistes — `/v1/g/:slug/artistes` (`docs/dev/api.md` § 13). Fiche FR/EN,
 * portrait (un média), contrat de représentation, ordre des œuvres.
 */
export interface Representation {
  readonly representee: boolean;
  readonly depuis: string | null;
  readonly jusquau: string | null;
  readonly exclusivite: boolean;
  readonly contratId: string | null;
}

export interface ArtistePresente {
  readonly id: string;
  readonly nom: string;
  readonly slug: string;
  readonly naissance: string | null;
  readonly nationalite: string | null;
  readonly biographie: Bilingue | null;
  readonly cv: Bilingue | null;
  readonly presse: Bilingue | null;
  readonly portraitId: string | null;
  readonly representation: Representation;
  readonly ordre: number;
  readonly ordreOeuvres: readonly string[];
  readonly visibleSurLeSite: boolean;
  readonly archive: boolean;
  readonly creeLe: string;
  readonly modifieLe: string;
  readonly nombreOeuvres?: number;
}

export interface CorpsArtiste {
  nom: string;
  slug?: string;
  naissance?: string;
  nationalite?: string;
  biographie?: { fr: string; en?: string };
  cv?: { fr: string; en?: string };
  presse?: { fr: string; en?: string };
  portraitId?: string | null;
  representation?: {
    representee?: boolean;
    depuis?: string;
    jusquau?: string;
    exclusivite?: boolean;
    contratId?: string | null;
  };
  ordre?: number;
  visibleSurLeSite?: boolean;
}

export interface FiltresArtistes {
  portraits?: boolean;
  q?: string;
  representee?: boolean;
  visibleSurSite?: boolean;
  inclureArchives?: boolean;
  tri?: 'ordre' | 'nom' | 'recent';
  page?: number;
  parPage?: number;
}

interface ReponseListeArtistes {
  artistes: readonly ArtistePresente[];
  pagination: Pagination;
  portraits?: ApercusSignes;
}

function requeteListe(filtres: FiltresArtistes): string {
  const params = new URLSearchParams();
  if (filtres.portraits) params.set('portraits', 'true');
  if (filtres.q) params.set('q', filtres.q);
  if (filtres.representee !== undefined) params.set('representee', String(filtres.representee));
  if (filtres.visibleSurSite !== undefined) {
    params.set('visibleSurSite', String(filtres.visibleSurSite));
  }
  if (filtres.inclureArchives) params.set('inclureArchives', 'true');
  if (filtres.tri) params.set('tri', filtres.tri);
  params.set('page', String(filtres.page ?? 1));
  params.set('parPage', String(filtres.parPage ?? 100));
  return params.toString();
}

export const artistesApi = {
  lister: (galerie: string, filtres: FiltresArtistes = {}, appel?: AppelApi) =>
    api.get<ReponseListeArtistes>(
      `/g/${encodeURIComponent(galerie)}/artistes?${requeteListe(filtres)}`,
      { galerie, ...appel },
    ),
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<{ artiste: ArtistePresente }>(`/g/${encodeURIComponent(galerie)}/artistes/${id}`, {
      galerie,
      ...appel,
    }),
  creer: (galerie: string, corps: CorpsArtiste, appel?: AppelApi) =>
    api.post<{ artiste: ArtistePresente }>(`/g/${encodeURIComponent(galerie)}/artistes`, corps, {
      galerie,
      ...appel,
    }),
  modifier: (galerie: string, id: string, corps: Partial<CorpsArtiste>, appel?: AppelApi) =>
    api.patch<{ artiste: ArtistePresente }>(
      `/g/${encodeURIComponent(galerie)}/artistes/${id}`,
      corps,
      { galerie, ...appel },
    ),
  archiver: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<{ artiste: ArtistePresente }>(
      `/g/${encodeURIComponent(galerie)}/artistes/${id}/archiver`,
      {},
      { galerie, ...appel },
    ),
  restaurer: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<{ artiste: ArtistePresente }>(
      `/g/${encodeURIComponent(galerie)}/artistes/${id}/restaurer`,
      {},
      { galerie, ...appel },
    ),
  ordonnerOeuvres: (galerie: string, id: string, oeuvres: readonly string[], appel?: AppelApi) =>
    api.put<{ artiste: ArtistePresente }>(
      `/g/${encodeURIComponent(galerie)}/artistes/${id}/ordre-oeuvres`,
      { oeuvres },
      { galerie, ...appel },
    ),
};

const cleListe = (galerie: string, filtres: FiltresArtistes) =>
  cleMetier(galerie, 'artistes', 'liste', filtres);
const cleFiche = (galerie: string, id: string) => cleMetier(galerie, 'artistes', 'fiche', id);

export function useArtistes(
  galerie: string,
  filtres: FiltresArtistes = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: cleListe(galerie, filtres),
    queryFn: (contexte) => artistesApi.lister(galerie, filtres, appelDeRequete(contexte)),
    enabled: options.enabled ?? true,
  });
}

export function useArtiste(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleFiche(galerie, id ?? ''),
    queryFn: (contexte) => artistesApi.obtenir(galerie, id ?? '', appelDeRequete(contexte)),
    select: (reponse) => reponse.artiste,
    enabled: id !== null,
  });
}

function invaliderArtistes(client: ReturnType<typeof useQueryClient>, galerie: string): void {
  void client.invalidateQueries({ queryKey: cleMetier(galerie, 'artistes') });
}

export function useCreerArtiste(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsArtiste, appel) => artistesApi.creer(galerie, corps, appel),
      onSuccess: () => invaliderArtistes(client, galerie),
    }),
  );
}

export function useModifierArtiste(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: Partial<CorpsArtiste> }, appel) =>
        artistesApi.modifier(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderArtistes(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useArchiverArtiste(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => artistesApi.archiver(galerie, id, appel),
      onSuccess: () => invaliderArtistes(client, galerie),
    }),
  );
}

export function useRestaurerArtiste(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => artistesApi.restaurer(galerie, id, appel),
      onSuccess: (_donnees, id) => {
        invaliderArtistes(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, id) });
      },
    }),
  );
}

export function useOrdonnerOeuvresArtiste(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, oeuvres }: { id: string; oeuvres: readonly string[] }, appel) =>
        artistesApi.ordonnerOeuvres(galerie, id, oeuvres, appel),
      // Optimisme (mission S3-a) : l'ordre affiché change avant la réponse du
      // serveur, et se rétablit si la mutation échoue.
      onMutate: async ({ id, oeuvres }) => {
        const cle = cleFiche(galerie, id);
        await client.cancelQueries({ queryKey: cle });
        const precedent = client.getQueryData<{ artiste: ArtistePresente }>(cle);
        if (precedent) {
          client.setQueryData(cle, {
            artiste: { ...precedent.artiste, ordreOeuvres: oeuvres },
          });
        }
        return { precedent };
      },
      onError: (_erreur, variables, contexte) => {
        if (contexte?.precedent)
          client.setQueryData(cleFiche(galerie, variables.id), contexte.precedent);
      },
      onSettled: (_donnees, _erreur, variables) => {
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export const cleListeArtistes = cleListe;
